import { border, ursaColors } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import * as colors from "../../../constants/colors";
import { themed, ThemeMode } from "../../../util/theme";

export const formInputTheme = themed({
    backgroundColor: {
        rest: {
            "lodestar-light": "transparent",
            "ursa-light": color("surface--work--background"),
        },
        readonly: {
            "lodestar-light": colors.neutralLight30,
            "ursa-light": color("surface--work--background"),
        },
        error: {
            "lodestar-light": "transparent",
            "ursa-light": color("surface--work--background"),
        },
        underlined: "transparent",
    },
    borderColor: {
        error: {
            "lodestar-light": colors.buttonDestructive,
            "ursa-light": color("form-input--border--error"),
        },
        // We have a disconnect between Lodestar and Ursa here. In Lodestar, the focus color is used
        // when the input is focused EVEN in the error state. This is becaus the border is the only
        // indication of focus or error. If it didn't get the focus treatment, you couldn't tell if
        // it was focused in the error state. In Ursa, however, the focus indicator is a ring around
        // the input so the border AND focus ring can show.
        errorFocus: {
            "lodestar-light": colors.focus,
            "ursa-light": color("form-input--border--error"),
        },
        focus: {
            "lodestar-light": colors.focus,
            oldestar: "#005ee0",
            "ursa-light": color("form-input--border--focus"),
        },
        hover: {
            "lodestar-light": colors.neutral,
            oldestar: "#a0a0a0",
            "ursa-light": color("form-input--border--hover"),
        },
        readonly: {
            "lodestar-light": colors.neutralLight30,
            "ursa-light": color("form-input--border--readonly"),
        },
        rest: {
            "lodestar-light": colors.neutralLight10,
            "ursa-light": color("form-input--border--rest"),
        },
    },
    borderWidth: {
        rest: {
            "lodestar-light": 2,
            oldestar: 1,
            "ursa-light": 1,
        },
        error: {
            "lodestar-light": 2,
            oldestar: 1,
            "ursa-light": 2,
        },
    },
    borderRadius: {
        "lodestar-light": border.radius10,
        oldestar: border.radius05,
        "ursa-light": border.radius30,
    },
    padding: {
        // This is used on the gap between the left bounding border, the contentLeft, the input,
        // and the contentRight
        left: {
            rest: {
                // 8px - 1px for the border.
                "lodestar-light": "10px",
                "ursa-light": "7px",
            },
            error: {
                // 8px - 2px for the thicker error border.
                "lodestar-light": "10px",
                "ursa-light": "6px",
            },
        },
        // This is used on the right side of the inputs as the gap between
        // the border and the input/contentRight's bounding box.
        right: {
            rest: {
                "lodestar-light": "6px",
                "ursa-light": "3px",
            },
            error: {
                // 4 - 2px for the thicker error border.
                "lodestar-light": "6px",
                "ursa-light": "2px",
            },
        },
    },
    textColor: {
        rest: {
            "lodestar-light": colors.neutralDark40,
            "ursa-light": color("surface--work--text"),
        },
        readonly: {
            "lodestar-light": colors.neutralDark40,
            "ursa-light": color("surface--work--text--inactive"),
        },
    },
    placeholderColor: {
        rest: {
            "lodestar-light": colors.neutralDark20,
            "ursa-light": color("surface--work--text--inactive"),
        },
        hover: {
            "lodestar-light": colors.neutralDark30,
            "ursa-light": color("surface--work--text--inactive-hover"),
        },
    },
    resizerColor: {
        "lodestar-light": colors.neutralDark10,
        "ursa-light": ursaColors.neutral300,
    },
    fontSize: {
        box: {
            // Inherit is needed here to override some UA styles which for some reason set the
            "ursa-light": "14px",
            // font-size to sub-px values like 13.333px. Chrome, why?
            "lodestar-light": "inherit",
        },
        underline: "15px",
    },
    fontWeight: {
        "lodestar-light": 500,
        oldestar: 400,
        "ursa-light": 400,
    },
    lineHeight: 18,
    focusRing: {
        offset: {
            "lodestar-light": 2,
            "ursa-light": 1,
        },
        enabled: {
            "lodestar-light": false,
            "ursa-light": true,
        },
    },
});

export interface TextInputRootBoxProps {
    isDisabled: boolean;
    isFocused: boolean;
    isHovered: boolean;
    themeMode: ThemeMode;
    validationState: "error" | undefined;
}

export function getBorderColor({
    validationState,
    isFocused,
    themeMode,
    isDisabled,
    isHovered,
}: TextInputRootBoxProps): string {
    const isError = validationState === "error";
    if (isFocused && isError) {
        return formInputTheme.borderColor.errorFocus(themeMode);
    } else if (isFocused) {
        return formInputTheme.borderColor.focus(themeMode);
    } else if (isError) {
        return formInputTheme.borderColor.error(themeMode);
    } else if (isDisabled) {
        return formInputTheme.borderColor.readonly(themeMode);
    } else if (isHovered) {
        return formInputTheme.borderColor.hover(themeMode);
    } else {
        return formInputTheme.borderColor.rest(themeMode);
    }
}

export function getBackgroundColor({ isDisabled, themeMode }: TextInputRootBoxProps): string {
    if (isDisabled) {
        return formInputTheme.backgroundColor.readonly(themeMode);
    } else {
        return formInputTheme.backgroundColor.rest(themeMode);
    }
}

export function getBorderWidth({ themeMode, validationState }: TextInputRootBoxProps): number {
    if (validationState === "error") {
        return formInputTheme.borderWidth.error(themeMode);
    } else {
        return formInputTheme.borderWidth.rest(themeMode);
    }
}
